import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Tag,
  Button,
  Space,
  message,
  Modal,
  Descriptions,
  Input,
  Select,
  Avatar,
  Tooltip,
} from 'antd';
import {
  MessageOutlined,
  UserOutlined,
  EyeOutlined,
  ReloadOutlined,
  CheckOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import apiClient from '../services/apiConfig';
import type { ColumnsType } from 'antd/es/table';

const { TextArea } = Input;
const { Option } = Select;

interface ContactInquiry {
  id: number;
  user_id: number;
  subject: 'stay' | 'reservation' | 'other';
  message: string;
  status: 'pending' | 'in_progress' | 'resolved';
  admin_response?: string;
  resolved_at?: string;
  created_at: string;
  updated_at: string;
  user: {
    id: number;
    name: string;
    last_name?: string;
    email: string;
    category?: string;
  };
}

const ContactInquiriesPage: React.FC = () => {
  const [inquiries, setInquiries] = useState<ContactInquiry[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState<ContactInquiry | null>(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [responseText, setResponseText] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    loadInquiries();
  }, []);

  const loadInquiries = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/contact-inquiries');
      if (response.data.success) {
        setInquiries(response.data.data);
      } else {
        message.error(response.data.message || 'Error al cargar las consultas');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error al cargar las consultas';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id: number, status: string, adminResponse?: string) => {
    try {
      setUpdatingStatus(true);
      await apiClient.put(`/contact-inquiries/${id}/status`, {
        status,
        admin_response: adminResponse,
      });
      message.success('Estado actualizado exitosamente');
      loadInquiries();
      if (detailsModalVisible) {
        setDetailsModalVisible(false);
        setResponseText('');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error al actualizar el estado';
      message.error(errorMessage);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const showDetails = (inquiry: ContactInquiry) => {
    setSelectedInquiry(inquiry);
    setResponseText(inquiry.admin_response || '');
    setDetailsModalVisible(true);
  };

  const getStatusTag = (status: string) => {
    const statusConfig = {
      pending: { color: 'warning', text: 'Pendiente' },
      in_progress: { color: 'processing', text: 'En Proceso' },
      resolved: { color: 'success', text: 'Resuelta' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getSubjectLabel = (subject: string) => {
    const labels = {
      stay: 'Consulta sobre Estadía',
      reservation: 'Reserva',
      other: 'Otro',
    };
    return labels[subject as keyof typeof labels] || subject;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns: ColumnsType<ContactInquiry> = [
    {
      title: 'Usuario',
      key: 'user',
      width: 250,
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} size="small" />
          <div>
            <div style={{ fontWeight: 500 }}>
              {record.user.name} {record.user.last_name || ''}
            </div>
            <div style={{ fontSize: '12px', color: '#888' }}>
              {record.user.email}
            </div>
          </div>
        </Space>
      ),
      filteredValue: searchText ? [searchText] : null,
      onFilter: (value, record) => {
        const searchValue = (value as string).toLowerCase();
        return (
          record.user.name.toLowerCase().includes(searchValue) ||
          record.user.email.toLowerCase().includes(searchValue) ||
          record.message.toLowerCase().includes(searchValue)
        );
      },
    },
    {
      title: 'Asunto',
      dataIndex: 'subject',
      key: 'subject',
      width: 180,
      render: (subject) => getSubjectLabel(subject),
    },
    {
      title: 'Mensaje',
      dataIndex: 'message',
      key: 'message',
      width: 400,
      render: (message) => (
        <div style={{ fontSize: '13px' }}>
          {message.substring(0, 100)}
          {message.length > 100 ? '...' : ''}
        </div>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      align: 'center',
      render: (status) => getStatusTag(status),
      filters: [
        { text: 'Pendiente', value: 'pending' },
        { text: 'En Proceso', value: 'in_progress' },
        { text: 'Resuelta', value: 'resolved' },
      ],
      filteredValue: statusFilter === 'all' ? null : [statusFilter],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Fecha',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => formatDate(date),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 250,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Ver detalles">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => showDetails(record)}
            />
          </Tooltip>

          {record.status === 'pending' && (
            <Button
              type="primary"
              size="small"
              icon={<ClockCircleOutlined />}
              onClick={() => handleUpdateStatus(record.id, 'in_progress')}
            >
              En Proceso
            </Button>
          )}

          {record.status === 'in_progress' && (
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              style={{ background: '#52c41a', borderColor: '#52c41a' }}
              onClick={() => showDetails(record)}
            >
              Resolver
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <MessageOutlined style={{ fontSize: '20px' }} />
            <span style={{ fontSize: '20px', fontWeight: 600 }}>
              Consultas de Contacto
            </span>
          </Space>
        }
        extra={
          <Space>
            <Select
              style={{ width: 150 }}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Filtrar por estado"
            >
              <Option value="all">Todos los estados</Option>
              <Option value="pending">Pendientes</Option>
              <Option value="in_progress">En Proceso</Option>
              <Option value="resolved">Resueltas</Option>
            </Select>

            <Input
              placeholder="Buscar por usuario o mensaje"
              allowClear
              style={{ width: 300 }}
              onChange={(e) => setSearchText(e.target.value)}
            />

            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={loadInquiries}
              loading={loading}
            >
              Actualizar
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={inquiries}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} consultas`,
          }}
        />
      </Card>

      {/* Modal de detalles */}
      <Modal
        title="Detalles de la Consulta"
        open={detailsModalVisible}
        onCancel={() => {
          setDetailsModalVisible(false);
          setResponseText('');
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setDetailsModalVisible(false);
              setResponseText('');
            }}
          >
            Cerrar
          </Button>,
          selectedInquiry?.status !== 'resolved' && (
            <Button
              key="resolve"
              type="primary"
              loading={updatingStatus}
              onClick={() =>
                selectedInquiry &&
                handleUpdateStatus(selectedInquiry.id, 'resolved', responseText)
              }
            >
              Marcar como Resuelta
            </Button>
          ),
        ]}
        width={800}
      >
        {selectedInquiry && (
          <>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Estado">
                {getStatusTag(selectedInquiry.status)}
              </Descriptions.Item>

              <Descriptions.Item label="Usuario">
                <Space>
                  <Avatar icon={<UserOutlined />} />
                  <div>
                    <div style={{ fontWeight: 500 }}>
                      {selectedInquiry.user.name} {selectedInquiry.user.last_name || ''}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                      {selectedInquiry.user.email}
                    </div>
                    {selectedInquiry.user.category && (
                      <Tag style={{ marginTop: 4 }}>
                        {selectedInquiry.user.category.toUpperCase()}
                      </Tag>
                    )}
                  </div>
                </Space>
              </Descriptions.Item>

              <Descriptions.Item label="Asunto">
                {getSubjectLabel(selectedInquiry.subject)}
              </Descriptions.Item>

              <Descriptions.Item label="Mensaje">
                <div style={{ whiteSpace: 'pre-wrap' }}>{selectedInquiry.message}</div>
              </Descriptions.Item>

              <Descriptions.Item label="Fecha de Consulta">
                {formatDate(selectedInquiry.created_at)}
              </Descriptions.Item>

              {selectedInquiry.resolved_at && (
                <Descriptions.Item label="Fecha de Resolución">
                  {formatDate(selectedInquiry.resolved_at)}
                </Descriptions.Item>
              )}
            </Descriptions>

            <div style={{ marginTop: 24 }}>
              <h4>Respuesta del Administrador</h4>
              {selectedInquiry.status === 'resolved' && selectedInquiry.admin_response ? (
                <div
                  style={{
                    padding: 12,
                    background: '#f5f5f5',
                    borderRadius: 8,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {selectedInquiry.admin_response}
                </div>
              ) : (
                <TextArea
                  rows={4}
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Escribe la respuesta o notas sobre esta consulta..."
                />
              )}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ContactInquiriesPage;
